import { useSearchParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';

/**
 * Hook for managing column visibility, persisted in URL params.
 * Hidden column keys are stored as comma-separated values.
 *
 * @param {Array} allColumns - Array of { key, label, defaultVisible?, alwaysVisible?, className? }
 * @param {string} paramKey - URL param name (default: 'hide')
 */
export function useColumnConfig(allColumns, paramKey = 'hide') {
  const [searchParams, setSearchParams] = useSearchParams();

  const hiddenKeys = useMemo(() => {
    const param = searchParams.get(paramKey);
    return param ? new Set(param.split(',')) : new Set();
  }, [searchParams, paramKey]);

  const visibleColumns = useMemo(() => {
    return allColumns.filter(col =>
      col.alwaysVisible || !hiddenKeys.has(col.key)
    );
  }, [allColumns, hiddenKeys]);

  const isVisible = useCallback((key) => {
    const col = allColumns.find(c => c.key === key);
    if (col?.alwaysVisible) return true;
    return !hiddenKeys.has(key);
  }, [allColumns, hiddenKeys]);

  const toggleColumn = useCallback((key) => {
    const col = allColumns.find(c => c.key === key);
    if (col?.alwaysVisible) return;

    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      const currentHidden = new Set(
        (prev.get(paramKey) || '').split(',').filter(Boolean)
      );

      if (currentHidden.has(key)) {
        currentHidden.delete(key);
      } else {
        currentHidden.add(key);
      }

      if (currentHidden.size === 0) {
        next.delete(paramKey);
      } else {
        next.set(paramKey, [...currentHidden].join(','));
      }
      return next;
    }, { replace: true });
  }, [allColumns, paramKey, setSearchParams]);

  return { visibleColumns, isVisible, toggleColumn, allColumns };
}
